/**
 * editor-accordion.js
 */

import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, alpha } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import usePrevious from 'utils/use-previous';
import Icon from 'components/icon';
import clsx from 'clsx';


const useStyles = makeStyles(theme => ({
  accordion: {
    flex: 1,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:before': {
      height: 0
    },
    '&.Mui-expanded': {
      marginTop: 0,
    },
    '&.accordion-highlighted': {
      margin: '0 -100px',
      padding: `${theme.spacing(1)} 100px`,
      transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
      '&:hover': {
        backgroundColor: alpha(theme.palette.grey.colors[1], 0.02),  
        [theme.breakpoints.down('sm_md')]: {
          backgroundColor: 'transparent'
        }
      },
      '& .MuiAccordionSummary-expandIconWrapper': {
        borderRadius : '50%',
        backgroundColor: alpha(theme.palette.grey.colors[1], 0.08),
      },
      '& .MuiAccordionSummary-content': {
        '& *': {
          fontWeight: '500!important'
        }
      } 
    }
  },
  accordionSummary: {
    alignItems: props => props.shiftButton ? 'flex-start' : 'center',

    padding: 0,
    '&.Mui-focused': {
      backgroundColor: 'initial',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      pointerEvents: 'none',
      margin: 0,
      alignSelf: 'flex-start',
      marginTop: props => props.shiftButton ? theme.spacing(1) : 0,
      padding: theme.spacing(1.5),
    }
  },
  accordionSummaryContent: {
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    }
  },
  accordionDetails: {
    padding: 0,
    flexDirection: 'column',
  }
}));


export default function EditorAccordion(props) {
  const {
    name,
    summary,
    detail,
    expand,
    onChange,
    highlight,
    className,
    contentClassName,
    shiftButton,
    ...rest
  } = props;
  const classes = useStyles({ shiftButton });
  const prevExpand = usePrevious(expand);
  const [open, setOpen] = React.useState(false);


  React.useEffect(() => {
    if (expand > 0 && expand !== prevExpand) {
      setOpen(true);
      if (onChange) onChange(true);
    }
  }, [expand, prevExpand, onChange]);


  return (
    <Accordion
      disableGutters
      expanded={open}
      classes={{
        root: classes.accordion
      }}
      className={clsx({'accordion-highlighted': highlight, [className]: true})}
      onChange={(e, v) => {
        if (onChange) onChange(v);
      }}
      {...rest}
    >
      <AccordionSummary
        expandIcon={<Icon type='expander' />}
        aria-label={`Editor Accordion ${name}`}
        aria-controls=''
        id={`editor-accordion-${name}`}
        onClick={e => setOpen(p => !p)}
        classes={{
          root: classes.accordionSummary,
          content: `${classes.accordionSummaryContent}${contentClassName ? ` ${contentClassName}` : ''}`,
        }}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails
        classes={{
          root: classes.accordionDetails
        }}
      >
        {detail}
      </AccordionDetails>
    </Accordion>
  );
}